import { useCallback, useEffect, useState } from "react";
import { apiGet, apiPost, apiPostForm } from "../api";
import { formatFrenchDay } from "../lib/days";
import { humanError } from "../lib/errors";
import { openOAuthUrl } from "../lib/oauth-window";
import { accountLabel, type LibraryItem, type SocialAccount, type SocialGroup, type SocialMedia, type SocialPost } from "../social";
import ConfirmRemove from "./ConfirmRemove";
import FadeModal from "./FadeModal";
import PostPreview from "./PostPreview";
import { DateField, TimeField } from "./fields";
import PublishCalendar, { postClock, postTitle } from "./PublishCalendar";
import PublishComposer from "./PublishComposer";

type Props = {
  onNotice: (msg: string) => void;
  onError: (msg: string) => void;
};

function accountKind(a: SocialAccount): string {
  if (a.network === "instagram") return "Instagram · compte relié";
  return "Facebook · Page reliée";
}

function groupMembers(group: SocialGroup, accounts: SocialAccount[]): string {
  const labels = group.accounts
    .map((id) => accounts.find((a) => a.id === id))
    .filter((a): a is SocialAccount => Boolean(a))
    .map((a) => accountLabel(a));
  return labels.length > 0 ? labels.join(" · ") : "Aucune Page";
}

function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export default function PublishPage({ onNotice, onError }: Props) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [groups, setGroups] = useState<SocialGroup[]>([]);
  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [library, setLibrary] = useState<LibraryItem[]>([]);
  const [day, setDay] = useState<string | null>(null);
  const [viewId, setViewId] = useState<string | null>(null);
  const [move, setMove] = useState<SocialPost | null>(null);
  const [moveDate, setMoveDate] = useState(todayIso());
  const [moveTime, setMoveTime] = useState("09:00");
  const [busy, setBusy] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [groupPick, setGroupPick] = useState<string[]>([]);

  const boot = useCallback(async () => {
    const [acc, po, lib, gr] = await Promise.all([
      apiGet<{ accounts: SocialAccount[] }>("meta-accounts.php"),
      apiGet<{ posts: SocialPost[] }>("meta-posts.php"),
      apiGet<{ items: LibraryItem[] }>("meta-library.php"),
      apiGet<{ groups: SocialGroup[] }>("meta-groups.php"),
    ]);
    setAccounts(acc.accounts || []);
    setPosts(po.posts || []);
    setLibrary(lib.items || []);
    setGroups(gr.groups || []);
  }, []);

  useEffect(() => {
    void boot().catch((err) => onError(humanError(err)));
  }, [boot, onError]);

  useEffect(() => {
    if (!day) return;
    void apiGet<{ items: LibraryItem[] }>("meta-library.php")
      .then((lib) => setLibrary(lib.items || []))
      .catch((err) => onError(humanError(err)));
  }, [day, onError]);

  const linked = accounts.filter((a) => a.source === "meta");
  const view = posts.find((p) => p.id === viewId) || null;
  const viewBoth = Boolean(
    view &&
      accounts.some((a) => view.accounts.includes(a.id) && a.network === "facebook") &&
      accounts.some((a) => view.accounts.includes(a.id) && a.network === "instagram"),
  );

  async function connectFacebook() {
    setConnecting(true);
    try {
      const data = await apiGet<{ url: string }>("meta-connect.php");
      if (!data.url) throw new Error("server");
      openOAuthUrl(data.url);
      setConnecting(false);
    } catch (err) {
      onError(humanError(err));
      setConnecting(false);
    }
  }

  async function addGroup() {
    setBusy(true);
    try {
      const data = await apiPost<{ groups: SocialGroup[] }>("meta-groups.php", { name: groupName, accounts: groupPick });
      setGroups(data.groups || []);
      setGroupName("");
      setGroupPick([]);
      onNotice("Groupe créé.");
    } catch (err) {
      onError(humanError(err));
    } finally {
      setBusy(false);
    }
  }

  async function removeGroup(id: string) {
    try {
      const data = await apiPost<{ groups: SocialGroup[] }>("meta-groups.php", { delete: id });
      setGroups(data.groups || []);
    } catch (err) {
      onError(humanError(err));
    }
  }

  async function removeAccount(id: string) {
    try {
      const data = await apiPost<{ accounts: SocialAccount[] }>("meta-accounts.php", { delete: id });
      setAccounts(data.accounts || []);
      setGroupPick((prev) => prev.filter((x) => x !== id));
    } catch (err) {
      onError(humanError(err));
    }
  }

  async function removeLibrary(id: string) {
    try {
      const data = await apiPost<{ items: LibraryItem[] }>("meta-library.php", { delete: id });
      setLibrary(data.items || []);
    } catch (err) {
      onError(humanError(err));
      throw err;
    }
  }

  async function upload(file: File): Promise<LibraryItem> {
    const form = new FormData();
    form.append("file", file);
    const data = await apiPostForm<{ item: LibraryItem; items: LibraryItem[] }>("meta-library.php", form);
    setLibrary(data.items || []);
    return data.item;
  }

  async function removePost(id: string) {
    try {
      const data = await apiPost<{ posts: SocialPost[] }>("meta-posts.php", { delete: id });
      setPosts(data.posts || []);
    } catch (err) {
      onError(humanError(err));
      throw err;
    }
  }

  async function duplicatePost(id: string) {
    try {
      const data = await apiPost<{ posts: SocialPost[] }>("meta-posts.php", { duplicate: id });
      setPosts(data.posts || []);
      onNotice("Post dupliqué.");
    } catch (err) {
      onError(humanError(err));
    }
  }

  function openMove(post: SocialPost) {
    setMove(post);
    setMoveDate(post.day || todayIso());
    setMoveTime(postClock(post) || "09:00");
  }

  async function saveMove() {
    if (!move) return;
    setBusy(true);
    try {
      const data = await apiPost<{ posts: SocialPost[] }>("meta-posts.php", {
        reschedule: move.id,
        scheduled_at: `${moveDate}T${moveTime}:00`,
      });
      setPosts(data.posts || []);
      setMove(null);
      onNotice("Post reprogrammé.");
    } catch (err) {
      onError(humanError(err));
    } finally {
      setBusy(false);
    }
  }

  async function savePost(payload: {
    caption: string;
    accounts: string[];
    media: SocialMedia[];
    when: "now" | "schedule";
    scheduled_at: string;
  }) {
    setBusy(true);
    try {
      const data = await apiPost<{ posts: SocialPost[] }>("meta-posts.php", payload);
      setPosts(data.posts || []);
      setDay(null);
      onNotice(payload.when === "schedule" ? "Post planifié." : "Post enregistré.");
    } catch (err) {
      onError(humanError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Publication</h2>
          <p className="mt-1 text-sm text-slate-500">Cliquez un jour pour écrire. Cliquez un post pour l’ouvrir.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="pr-btn" onClick={() => setDay(todayIso())}>
            Nouvelle publication
          </button>
          {linked.length > 0 ? (
            <span className="pr-chip is-on inline-flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-violet" aria-hidden />
              {linked.length === 1 ? `${linked[0].name} · connecté` : `${linked.length} Pages · connectées`}
            </span>
          ) : null}
          <button type="button" className="pr-btn-ghost" disabled={connecting} onClick={() => void connectFacebook()}>
            {connecting ? "Redirection…" : "Connecter une Page"}
          </button>
        </div>
      </div>

      <PublishCalendar
        year={year}
        month={month}
        posts={posts}
        selected={day || view?.day}
        onMonth={(y, m) => {
          setYear(y);
          setMonth(m);
        }}
        onDay={setDay}
        onOpen={(post) => setViewId(post.id)}
      />

      <section className="pr-card p-5">
        <h3 className="text-sm font-semibold">Destinations</h3>
        <p className="mt-1 text-xs text-slate-500">Une publication part vers une Page, ou vers un groupe de Pages.</p>
        <div className="mt-4 grid gap-6 lg:grid-cols-2">
          <div>
            <p className="text-sm font-semibold">Pages reliées</p>
            <p className="mt-1 text-xs text-slate-500">Les Pages Facebook connectées à Présence.</p>
            {linked.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">Aucune Page reliée.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {linked.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 px-3 py-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{a.name.trim() || accountLabel(a)}</span>
                      <span className="block text-xs text-slate-500">{accountKind(a)}</span>
                    </span>
                    <ConfirmRemove question="Retirer cette Page ?" onConfirm={() => removeAccount(a.id)} />
                  </li>
                ))}
              </ul>
            )}
            <button type="button" className="pr-btn mt-4" disabled={connecting} onClick={() => void connectFacebook()}>
              {connecting ? "Redirection…" : "Connecter une Page"}
            </button>
          </div>
          <div>
            <p className="text-sm font-semibold">Groupes</p>
            <p className="mt-1 text-xs text-slate-500">Un groupe rassemble des Pages pour les choisir ensemble.</p>
            {groups.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">Aucun groupe.</p>
            ) : (
              <ul className="mt-3 space-y-2">
                {groups.map((g) => (
                  <li key={g.id} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-100 px-3 py-2">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{g.name}</span>
                      <span className="block text-xs text-slate-500">{groupMembers(g, accounts)}</span>
                    </span>
                    <ConfirmRemove question="Retirer ce groupe ?" onConfirm={() => removeGroup(g.id)} />
                  </li>
                ))}
              </ul>
            )}
            <form
              className="mt-4 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                void addGroup();
              }}
            >
              <label className="block text-xs text-slate-500">
                Nom du groupe
                <input value={groupName} onChange={(e) => setGroupName(e.target.value)} className="pr-input mt-1" placeholder="ex. Studio Seth" />
              </label>
              {linked.length === 0 ? (
                <p className="text-xs text-slate-500">Reliez une Page avant de créer un groupe.</p>
              ) : (
                <div>
                  <p className="mb-2 text-xs text-slate-500">Pages du groupe</p>
                  <div className="flex flex-wrap gap-2">
                    {linked.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => setGroupPick((prev) => (prev.includes(a.id) ? prev.filter((x) => x !== a.id) : [...prev, a.id]))}
                        className={`pr-chip ${groupPick.includes(a.id) ? "is-on" : "is-off"}`}
                      >
                        {a.name.trim() || accountLabel(a)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <button type="submit" disabled={busy || linked.length === 0} className="pr-btn">
                Créer le groupe
              </button>
            </form>
          </div>
        </div>
      </section>

      <FadeModal open={Boolean(day)} onClose={() => setDay(null)} wide>
        {day ? (
          <PublishComposer
            day={day}
            accounts={linked}
            groups={groups}
            library={library}
            busy={busy}
            onClose={() => setDay(null)}
            onError={(msg) => onError(msg)}
            onUpload={upload}
            onDeleteLibrary={removeLibrary}
            onSave={savePost}
          />
        ) : null}
      </FadeModal>

      <FadeModal open={Boolean(view)} onClose={() => setViewId(null)} panel={viewBoth ? "max-w-3xl" : "max-w-md"}>
        {view ? (
          <PostDialog
            post={view}
            siblings={posts.filter((p) => (p.day || "") === (view.day || "") && p.id !== view.id)}
            accounts={accounts}
            onPick={setViewId}
            onDuplicate={() => void duplicatePost(view.id)}
            onReschedule={() => openMove(view)}
            onDelete={async () => {
              await removePost(view.id);
              setViewId(null);
            }}
            onClose={() => setViewId(null)}
          />
        ) : null}
      </FadeModal>

      <FadeModal open={Boolean(move)} onClose={() => setMove(null)} top>
        {move ? (
          <section className="pr-card w-full p-6">
            <h2 className="text-lg font-semibold">Reprogrammer</h2>
            <p className="mt-1 text-sm text-slate-500">Choisissez le nouveau jour et la nouvelle heure.</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <p className="mb-1 text-xs text-slate-500">Jour</p>
                <DateField value={moveDate} onChange={setMoveDate} />
              </div>
              <div>
                <p className="mb-1 text-xs text-slate-500">Heure</p>
                <TimeField value={moveTime} onChange={setMoveTime} />
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <button type="button" className="pr-btn" disabled={busy} onClick={() => void saveMove()}>
                {busy ? "Enregistrement…" : "Enregistrer"}
              </button>
              <button type="button" className="pr-btn-ghost" onClick={() => setMove(null)}>
                Annuler
              </button>
            </div>
          </section>
        ) : null}
      </FadeModal>
    </div>
  );
}

function PostDialog({
  post,
  siblings,
  accounts,
  onPick,
  onDuplicate,
  onReschedule,
  onDelete,
  onClose,
}: {
  post: SocialPost;
  siblings: SocialPost[];
  accounts: SocialAccount[];
  onPick: (id: string) => void;
  onDuplicate: () => void;
  onReschedule: () => void;
  onDelete: () => Promise<void>;
  onClose: () => void;
}) {
  const [ask, setAsk] = useState(false);
  const chosen = post.accounts
    .map((id) => accounts.find((a) => a.id === id))
    .filter((a): a is SocialAccount => Boolean(a));
  const fb = chosen.find((a) => a.network === "facebook");
  const ig = chosen.find((a) => a.network === "instagram");
  const frameId = post.media.find((m) => m.frame)?.frame || "1:1";
  return (
    <section className="pr-card p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Publication</h2>
          <p className="mt-1 text-sm text-slate-500">
            {formatFrenchDay(post.day)}
            {postClock(post) ? ` · ${postClock(post)}` : ""}
            {" · "}
            {post.status === "scheduled" ? "Planifié" : "Enregistré"}
          </p>
        </div>
        <button type="button" className="pr-btn-ghost" onClick={onClose}>
          Fermer
        </button>
      </div>
      <div className={`mt-5 grid gap-4 ${fb && ig ? "sm:grid-cols-2" : ""}`}>
        {fb || !ig ? (
          <PostPreview network="facebook" name={fb?.name || "Facebook"} caption={post.caption} media={post.media} frameId={frameId} />
        ) : null}
        {ig ? <PostPreview network="instagram" name={ig.name || "Instagram"} caption={post.caption} media={post.media} frameId={frameId} /> : null}
      </div>
      {siblings.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-2">
          {siblings.map((other) => (
            <button key={other.id} type="button" className="pr-chip is-off" onClick={() => onPick(other.id)}>
              {postClock(other) || "—"} {postTitle(other)}
            </button>
          ))}
        </div>
      ) : null}
      {ask ? (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <span className="text-sm text-slate-600">Supprimer ce post ?</span>
          <button type="button" className="pr-btn" onClick={() => void onDelete()}>
            Supprimer
          </button>
          <button type="button" className="pr-btn-ghost" onClick={() => setAsk(false)}>
            Annuler
          </button>
        </div>
      ) : (
        <div className="mt-5 flex flex-wrap justify-center gap-2 border-t border-slate-100 pt-4">
          <button type="button" className="pr-btn-line" onClick={onDuplicate}>
            Dupliquer
          </button>
          <button type="button" className="pr-btn-line" onClick={onReschedule}>
            Reprogrammer
          </button>
          <button type="button" className="pr-btn-danger" onClick={() => setAsk(true)}>
            Supprimer
          </button>
        </div>
      )}
    </section>
  );
}
